import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";

export type PortalSelectOption = {
	value: string;
	label: string;
	description?: string;
	disabled?: boolean;
};

type PortalSelectProps = {
	id?: string;
	label: ReactNode;
	ariaLabel?: string;
	value: string;
	options: readonly PortalSelectOption[];
	onChange: (value: string) => void;
	description?: ReactNode;
	className?: string;
	disabled?: boolean;
	required?: boolean;
};

export function PortalSelect({
	id,
	label,
	ariaLabel,
	value,
	options,
	onChange,
	description,
	className,
	disabled = false,
	required = false,
}: PortalSelectProps) {
	const generatedId = useId();
	const controlId = id ?? `portal-select-${generatedId}`;
	const labelId = `${controlId}-label`;
	const listboxId = `${controlId}-listbox`;
	const rootRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const optionRefs = useRef(new Map<string, HTMLButtonElement>());
	const pendingFocusValue = useRef<string | undefined>(undefined);
	const [open, setOpen] = useState(false);
	const [menuMaxHeight, setMenuMaxHeight] = useState(288);
	const selectedOption = useMemo(() => options.find((option) => option.value === value), [options, value]);
	const accessibleLabel = ariaLabel ?? (typeof label === "string" ? label : "Choose an option");
	const enabledOptions = useMemo(() => options.filter((option) => !option.disabled), [options]);

	useEffect(() => {
		if (!open) return;
		function closeForOutsidePress(event: PointerEvent) {
			if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
		}
		document.addEventListener("pointerdown", closeForOutsidePress);
		return () => document.removeEventListener("pointerdown", closeForOutsidePress);
	}, [open]);

	useEffect(() => {
		if (!open) return;
		const focusValue = pendingFocusValue.current ?? selectedOption?.value ?? enabledOptions[0]?.value;
		pendingFocusValue.current = undefined;
		if (!focusValue) return;
		requestAnimationFrame(() => optionRefs.current.get(focusValue)?.focus({ preventScroll: true }));
	}, [enabledOptions, open, selectedOption]);

	function openMenu(focusValue?: string) {
		if (disabled || enabledOptions.length === 0) return;
		const triggerRect = triggerRef.current?.getBoundingClientRect();
		if (triggerRect) {
			setMenuMaxHeight(Math.max(44, Math.min(288, window.innerHeight - triggerRect.bottom - 16)));
		}
		pendingFocusValue.current = focusValue;
		setOpen(true);
	}

	function closeMenu(returnFocus = false) {
		setOpen(false);
		if (returnFocus) requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
	}

	function choose(option: PortalSelectOption) {
		if (option.disabled) return;
		onChange(option.value);
		closeMenu(true);
	}

	function moveOptionFocus(currentValue: string, direction: -1 | 1) {
		const currentIndex = enabledOptions.findIndex((option) => option.value === currentValue);
		const nextIndex = (currentIndex + direction + enabledOptions.length) % enabledOptions.length;
		const nextValue = enabledOptions[nextIndex]?.value;
		if (nextValue) optionRefs.current.get(nextValue)?.focus({ preventScroll: true });
	}

	return (
		<div className={`portal-select${className ? ` ${className}` : ""}`} ref={rootRef}>
			<span className="portal-select-label" id={labelId}>
				{label}
			</span>
			<div className="portal-select-anchor">
				<button
					className="portal-select-trigger"
					id={controlId}
					ref={triggerRef}
					type="button"
					disabled={disabled}
					aria-label={`${accessibleLabel}. Current: ${selectedOption?.label ?? "none selected"}`}
					aria-haspopup="listbox"
					aria-expanded={open}
					aria-controls={listboxId}
					onClick={() => (open ? closeMenu() : openMenu())}
					onKeyDown={(event) => {
						if (event.key === "ArrowDown" || event.key === "ArrowUp") {
							event.preventDefault();
							openMenu(event.key === "ArrowUp" ? enabledOptions.at(-1)?.value : enabledOptions[0]?.value);
						}
					}}
				>
					<span className="portal-select-value">
						<span>{selectedOption?.label ?? "Choose…"}</span>
						{selectedOption?.description && <small>{selectedOption.description}</small>}
					</span>
				</button>
				{open && (
					<div
						className="portal-select-menu"
						id={listboxId}
						role="listbox"
						aria-labelledby={labelId}
						aria-required={required || undefined}
						style={{ maxHeight: `${menuMaxHeight}px` }}
					>
						{options.map((option) => (
							<button
								type="button"
								key={option.value}
								ref={(element) => {
									if (element) optionRefs.current.set(option.value, element);
									else optionRefs.current.delete(option.value);
								}}
								role="option"
								aria-selected={option.value === value}
								disabled={option.disabled}
								onClick={() => choose(option)}
								onKeyDown={(event) => {
									if (event.key === "Escape") {
										event.preventDefault();
										closeMenu(true);
									} else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
										event.preventDefault();
										moveOptionFocus(option.value, event.key === "ArrowDown" ? 1 : -1);
									} else if (event.key === "Home" || event.key === "End") {
										event.preventDefault();
										const target = event.key === "Home" ? enabledOptions[0] : enabledOptions.at(-1);
										if (target) optionRefs.current.get(target.value)?.focus({ preventScroll: true });
									}
								}}
							>
								<span className="portal-select-option-copy">
									<span>{option.label}</span>
									{option.description && <small>{option.description}</small>}
								</span>
								{option.value === value && <span aria-hidden="true">✓</span>}
							</button>
						))}
					</div>
				)}
			</div>
			{description && <small className="portal-select-description">{description}</small>}
		</div>
	);
}
